'use client';
import { pushErrorData, resetErrors } from '@/redux/errorSlice';
import { pushFlowData } from '@/redux/flowSlice';
import { pushErrorReason, showModal } from '@/redux/navigationSlice';
import { RootState } from '@/redux/store';
import { ButtonProps } from '@mui/material';
import { createInstance, getInstance, step, submitStep } from '@repo/onb-api';
import { Form } from '@repo/ui';
import { getClientCookie, setClientCookie } from '@repo/utils';
import { omit } from 'lodash';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendOnbEvent } from '../newRelic';
import { formProps } from './types';

/**
 * Renders an onboarding form component with step submit logic.
 *
 * @param {string} stepName - The name of the step.
 * @param {boolean} hideSubmit - Whether to hide the submit button in order to handle it manually.
 * @param {string} [submitLabel] - The label for the submit button default: continuar
 * @param {string} [cancelLabel] - The label for the cancel button.
 * @param {Object} [rest] - Additional props for the Form component.
 * @param {boolean} [forceInternal] - Force the form to be internal.
 * @param {(keyof variables)[]} [submitBlacklist] - A list of fields to blacklist for the submit data.
 * @return {React.JSX.Element} The rendered OnbForm component.
 */
export const OnbForm = <T,>({
  stepName,
  hideSubmit,
  submitLabel,
  cancelLabel,
  disabled,
  forceInternal,
  submitBlacklist,
  ...rest
}: formProps): React.JSX.Element => {
  const additionalData = useSelector((state: RootState) => state.additionalData);
  const { steps, accessKey } = useSelector((state: RootState) => state.flowdata);
  let instanceId = getClientCookie('instanceId');
  const dispatch = useDispatch();
  const router = useRouter();
  const [submitting, setSubmitting] = useState(false);
  const variables = steps?.find((step) => step.name === stepName)?.variables || {};
  const isInitialized = steps?.find((step) => step.name === stepName)?.initialized || false;
  const internal =
    forceInternal !== undefined ? forceInternal : useSelector((state: RootState) => state.additionalData.internal);

  // const date = new Date();
  // date.setTime(date.getTime() + 3 * 60 * 60 * 1000); // Adding 3 hours in milliseconds
  // const cookieTtl = date.toUTCString();
  const buttons: ButtonProps[] = [
    {
      id: `${stepName}-submit`,
      name: 'submit',
      disabled: disabled,
      sx: { display: hideSubmit ? 'none' : 'block' },
      children: submitLabel ? submitLabel : 'Continuar',
    },
  ];

  if (!internal) {
    buttons.push({
      name: 'cancel',
      // disabled: submitting,
      children: cancelLabel
        ? cancelLabel
        : process.env.NEXT_PUBLIC_ONB_INSTANCE_TYPE === 'onb-cuotas'
          ? 'Volver a la tienda'
          : 'Volver al Inicio',
      onClick:
        process.env.NEXT_PUBLIC_ONB_INSTANCE_TYPE === 'onb-cuotas'
          ? () => {
              dispatch(
                showModal({
                  body: 'backToStore',
                  title: 'Si cancelas, todos los datos ingresados se perderán. ¿Estás seguro?',
                }),
              );
            }
          : () => {
              dispatch(
                showModal({
                  body: 'cancel',
                  title: 'Si volvés al inicio todos los datos ingresados se perderán',
                }),
              );
            },
    });
  }
  const submitHandler = ({ retry, instanceId }: { retry: number; instanceId: string }) => {
    let redirectStep = '';
    submitStep<T>({
      instanceId,
      step: {
        name: stepName,
        variables: submitBlacklist ? omit(variables, submitBlacklist) : variables,
        accessKey: accessKey,
      } as T,
    })
      .then((response) => {
        if (response.error === 'STEP_MISMATCH') {
          return getInstance({ instanceId, accessKey }).then((res) => {
            dispatch(pushFlowData(res));
            dispatch(resetErrors());
            redirectStep = res?.steps?.[res?.steps?.length - 1]?.name
              ? `/flow/${res?.steps?.[res?.steps?.length - 1]?.name}`
              : '/error';
            router.push(redirectStep);
          });
        }

        if (response?.hardError) {
          sendOnbEvent({
            type: 'error',
            subtype: 'submitStep',
            data: { error: response?.error, stepName, instanceId },
            severity: 'high',
          });
          if (retry > 0) {
            submitHandler({ retry: retry - 1, instanceId });
          } else {
            redirectStep = '/error';
          }
        } else if (response?.softError) {
          sendOnbEvent({
            type: 'error',
            subtype: 'submitStep',
            data: { attribute: response?.attribute, error: response?.error, stepName, instanceId },
            severity: 'low',
          });
          dispatch(pushErrorData({ attribute: response?.attribute, error: response?.error }));
          if (response.error === 'INVALID_STEP') {
            redirectStep = '/error';
          }
          setSubmitting(false);
        } else {
          dispatch(pushFlowData(response));
          dispatch(resetErrors());
          redirectStep = response?.steps?.[response?.steps?.length - 1]?.name
            ? `/flow/${response?.steps?.[response?.steps?.length - 1]?.name}`
            : '/error';
        }
      })
      .catch((error) => {
        sendOnbEvent({
          type: 'error',
          subtype: 'submitStep',
          data: { error: error?.error, stepName, instanceId },
          severity: 'high',
        });
        redirectStep = '/error';
      })
      .finally(() => {
        if (redirectStep !== '') router.push(redirectStep);
      });
  };

  return (
    <Form
      submitting={submitting}
      formProps={{
        onSubmit: async () => {
          try {
            setSubmitting(true);
            dispatch(resetErrors());
            if (!instanceId) {
              if (isInitialized) {
                dispatch(pushErrorReason('timeout'));
                sendOnbEvent({
                  type: 'error',
                  subtype: 'timeout',
                  severity: 'low',
                  data: { stepName },
                });
                router.push('/error');
              }
              /** If no instance present on redux creates a new one before submition */
              try {
                const initialData = await createInstance({ additionalData });
                if (initialData.hardError) {
                  sendOnbEvent({
                    type: 'error',
                    subtype: 'createInstance',
                    severity: 'high',
                    data: { error: initialData?.message, stepName },
                  });
                  router.push('/error');
                } else {
                  sendOnbEvent({
                    type: 'serverEvent',
                    subtype: 'createInstance',
                    data: { stepName },
                  });
                  setClientCookie('instanceId', initialData.id);
                  // setClientCookie('instanceId', initialData.id, cookieTtl);
                  dispatch(
                    pushFlowData({
                      ...initialData,
                      steps: initialData?.steps?.map((s) =>
                        s.name === stepName ? ({ ...s, variables: variables } as step) : s,
                      ),
                    }),
                  );
                  if (initialData.steps?.[(initialData.steps?.length || 1) - 1]?.name !== stepName) {
                    sendOnbEvent({
                      type: 'serverEvent',
                      subtype: 'preInstanceRevalidation',
                      data: { stepName },
                    });
                    await fetch(`${process.env.NEXT_PUBLIC_URL}/revalidate?tag=landingData`, { cache: 'no-store' });
                    router.push(
                      initialData.steps?.[initialData?.steps?.length - 1]?.name
                        ? `flow/${initialData.steps?.[initialData?.steps?.length - 1]?.name}`
                        : 'error',
                    );
                  }
                  instanceId = initialData.id;
                }
              } catch (error: any) {
                sendOnbEvent({
                  type: 'error',
                  subtype: 'createInstance',
                  data: { error: error?.error, stepName },
                  severity: 'high',
                });
                router.push('/error');
              }
            }
            if (instanceId) {
              submitHandler({ retry: 0, instanceId });
            }
          } catch (error: any) {
            console.error('Error 1969', error);
            //@todo - check if event is always sending.
            sendOnbEvent({
              type: 'error',
              subtype: 'submitStep',
              data: { error: error?.error, stepName },
              severity: 'high',
            });
            router.push('/error');
          }
        },
        ...rest,
      }}
      buttonProps={buttons}
    />
  );
};
